import React, {
  useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  FormControlLabel, Radio, RadioGroup, Input,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { changeTrip, changeValidation } from '../../../store/slices/trip';
import TwExpansionPanel from '../../../HOCs/TwExpansionPanel';
import useStyles from '../CruiseConfig/styles';
import ErrorTooltip from '../../ErrorTooltip';
import BookingItemTitle from '../BookingItemTitle';
import { CheckoutFieldPlacement, logErrorToConsole } from '../../../constants';
import SearchAutocomplete from '../../SearchAutocomplete';

export const shouldShowField = (field, selectedFields) => {
  if (!field.showWhen) return true;

  return field.showWhen.split(',').some((condition) => {
    const [conditionKey, expectedValue] = condition.split('=');
    return selectedFields[conditionKey] === expectedValue;
  });
};

const getSelectedOption = (options, selectedValue) => options?.find((option) => option.value === selectedValue) || '';

const AdditionalCheckoutFields = ({
  doDisableTravelerInputs, fieldPlacement,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { selectedAdditionalCheckoutFields = {}, tripConfirmation } = useSelector(
    (store) => store.trip, shallowEqual,
  );
  const { invalidAdditionalCheckoutFields = {} } = useSelector((store) => store.trip);
  const { travelAgency = {} } = useSelector((store) => store.user);

  const checkoutFields = useMemo(
    () => (travelAgency.checkoutFields?.length > 0
      ? travelAgency.checkoutFields.filter(
        (f) => !fieldPlacement || f.showWhere === fieldPlacement,
      ) : []),
    [fieldPlacement, travelAgency.checkoutFields],
  );

  useEffect(() => {
    checkoutFields.forEach((field) => {
      const value = selectedAdditionalCheckoutFields[field.key];
      if (field.inputType === 'Radio' && shouldShowField(field, selectedAdditionalCheckoutFields)
        && (value === undefined || value === '') && field.defaultValue) {
        dispatch(changeTrip({
          field: 'selectedAdditionalCheckoutFields',
          value: { ...selectedAdditionalCheckoutFields, [field.key]: field.defaultValue },
        }));
      }
    });
    if (tripConfirmation?.customBookingFields) {
      let customFields = {};
      try {
        customFields = JSON.parse(tripConfirmation.customBookingFields || '{}');
      } catch (error) {
        logErrorToConsole('Invalid JSON in customBookingFields:', error);
        customFields = {};
      }
      Object.entries(customFields).forEach(([key, value]) => {
        if (!selectedAdditionalCheckoutFields?.[key]) {
          dispatch(changeTrip({
            field: 'selectedAdditionalCheckoutFields',
            value: {
              ...selectedAdditionalCheckoutFields,
              [key]: value,
            },
          }));
        }
      });
    }
  }, [checkoutFields, selectedAdditionalCheckoutFields, dispatch, tripConfirmation]);

  const getInputValue = useCallback((field) => JSON.parse(tripConfirmation?.customBookingFields ?? '{}')?.[field?.key]?.toString() ?? selectedAdditionalCheckoutFields[field.key], [selectedAdditionalCheckoutFields, tripConfirmation]);

  const validateAndDispatchCheckoutFields = useCallback(
    (checkoutFieldName, value) => {
      let updatedValues = { ...selectedAdditionalCheckoutFields, [checkoutFieldName]: value };

      if (value === null || value === '') {
        const { [checkoutFieldName]: _, ...rest } = updatedValues;
        updatedValues = rest;
      }

      Object.entries(updatedValues).filter(([key]) => {
        if (checkoutFields.some((f) => f.key === key && shouldShowField(f, updatedValues))) {
          return true;
        }
        updatedValues[key] = undefined;
        return false;
      });

      dispatch(changeTrip({ field: 'selectedAdditionalCheckoutFields', value: updatedValues }));
      return updatedValues;
    },
    [checkoutFields, dispatch, selectedAdditionalCheckoutFields],
  );

  const renderField = useCallback((field) => {
    switch (field.inputType) {
      case 'Radio':
        return (
          <RadioGroup
            onChange={(event) => validateAndDispatchCheckoutFields(
              field.key,
              event?.target?.value,
              field,
            )}
            row
            value={getInputValue(field)?.toString() ?? field.defaultValue ?? ''}
          >
            {Object.keys(field.options || {}).map((key) => (
              <FormControlLabel
                value={field.options[key]}
                control={<Radio />}
                label={key}
                disabled={doDisableTravelerInputs}
                key={key}
              />
            ))}
          </RadioGroup>
        );
      case 'Text':
        return (
          <div>
            <Input
              disabled={doDisableTravelerInputs}
              type="text"
              placeholder={field.fieldName}
              className="px-6 py-3 rounded-2xl bg-gray-500 bg-opacity-5 placeholder-gray-500F w-full"
              classes={classes}
              disableUnderline
              value={getInputValue(field)?.toString() ?? selectedAdditionalCheckoutFields[field.key] ?? field?.defaultValue ?? ''}
              onChange={(e) => validateAndDispatchCheckoutFields(
                field.key,
                e?.target?.value,
                field,
              )}
            />
          </div>
        );
      default: {
        const options = Object.keys(field.options || {}).map((key) => ({
          value: field.options[key],
          label: key,
        }));
        return options && options.length ? (
          <div>
            <SearchAutocomplete
              disabled={doDisableTravelerInputs}
              placeholderText={field.fieldName + (field.isRequiredField ? ' (required)' : '')}
              options={[{ suggests: options }]}
              values={getSelectedOption(options, getInputValue(field)?.toString()
                ?? selectedAdditionalCheckoutFields[field.key])}
              onChange={(selectedValue) => validateAndDispatchCheckoutFields(
                field.key,
                selectedValue?.value,
                field,
              )}
              autoFocus={false}
              freeSolo={false}
            />
          </div>
        ) : <></>;
      }
    }
  }, [classes, doDisableTravelerInputs, validateAndDispatchCheckoutFields,
    selectedAdditionalCheckoutFields, getInputValue]);

  const handleFormValidationsReset = useCallback(() => {
    dispatch(changeValidation({
      invalidAdditionalCheckoutFields: {},
    }));
  }, [dispatch]);

  const renderFields = useMemo(() => {
    if (!checkoutFields || !checkoutFields.length) return <></>;

    return (
      <div
        className={clsx({
          'grid grid-cols-2 gap-4': fieldPlacement === CheckoutFieldPlacement.PrimaryPassenger,
          'mb-4 m-4 sm:mx-6': fieldPlacement !== CheckoutFieldPlacement.PrimaryPassenger,
        })}
        onFocus={handleFormValidationsReset}
      >
        {checkoutFields.filter((f) => shouldShowField(f, selectedAdditionalCheckoutFields)
          || Object.keys(JSON.parse(tripConfirmation?.customBookingFields || '{}'))?.includes(f?.key))
          .map((field) => {
            const inputVal = getInputValue(field)?.toString() ?? field.defaultValue ?? '';
            const selectedOption = getSelectedOption(
              Object.entries(field.options || {}).map(([label, value]) => ({ label, value })),
              inputVal,
            );
            return (
              <div key={field.key} className="flex items-center mb-4">
                {fieldPlacement !== CheckoutFieldPlacement.PrimaryPassenger && (
                <div className="w-full sm:w-2/5 pr-2 mb-2 sm:mb-0">
                  {field.fieldName}
                  {field.isRequiredField && ' (required)'}
                </div>
                )}
                <div className="w-full">
                  <ErrorTooltip
                    title={`Please provide valid ${field.fieldName}`}
                    open={!!(invalidAdditionalCheckoutFields
                      && invalidAdditionalCheckoutFields[field.key])}
                    placement="left"
                    arrow
                    PopperProps={{ disablePortal: true }}
                  >
                    {renderField(field, selectedOption)}
                  </ErrorTooltip>
                </div>
              </div>
            );
          })}
      </div>
    );
  }, [checkoutFields, fieldPlacement, getInputValue, handleFormValidationsReset,
    invalidAdditionalCheckoutFields, renderField, selectedAdditionalCheckoutFields,
    tripConfirmation]);

  if (!checkoutFields || !checkoutFields.length) return <></>;

  return fieldPlacement === CheckoutFieldPlacement.PrimaryPassenger ? renderFields : (
    <TwExpansionPanel>
      <div className="text-grey-700 mb-5" id="additional-checkout-fields">
        <BookingItemTitle placeholder="Additional Details" />
        {renderFields}
      </div>
    </TwExpansionPanel>
  );
};

AdditionalCheckoutFields.propTypes = {
  doDisableTravelerInputs: PropTypes.bool,
  fieldPlacement: PropTypes.string,
};

AdditionalCheckoutFields.defaultProps = {
  doDisableTravelerInputs: false,
  fieldPlacement: undefined,
};

export default AdditionalCheckoutFields;
